import { ConfigProvider, ThemeConfig } from 'antd';
import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useLayoutEffect,
  useMemo,
  useState,
} from 'react';
import { DEFAULT_THEME, isThemeId, THEME_STORAGE_KEY, ThemeId, THEMES } from 'config/themes';

interface ThemeContextValue {
  theme: ThemeId;
  themes: typeof THEMES;
  setTheme: (theme: ThemeId) => void;
}

const ThemeContext = createContext<ThemeContextValue | null>(null);

const readStoredTheme = (): ThemeId | null => {
  try {
    const saved = localStorage.getItem(THEME_STORAGE_KEY);
    return isThemeId(saved) ? saved : null;
  } catch {
    return null;
  }
};

const storeTheme = (theme: ThemeId) => {
  try {
    localStorage.setItem(THEME_STORAGE_KEY, theme);
  } catch {
    // приватный режим браузера: тема просто не запомнится
  }
};

// antd считает оттенки сам, поэтому ему нужны готовые значения, а не var(--…).
// Берём их из CSS-переменных, которые задают палитры: цвета остаются в одном месте.
const readAntdTheme = (): ThemeConfig => {
  const css = getComputedStyle(document.documentElement);
  const cssVar = (name: string) => css.getPropertyValue(name).trim();
  const px = (name: string) => parseInt(cssVar(name), 10) || undefined;

  return {
    token: {
      colorPrimary: cssVar('--color-primary'),
      colorLink: cssVar('--color-primary'),
      colorInfo: cssVar('--color-primary'),
      colorSuccess: cssVar('--color-success'),
      colorWarning: cssVar('--color-warning'),
      colorError: cssVar('--color-danger'),
      colorText: cssVar('--color-text'),
      colorTextSecondary: cssVar('--color-text-muted'),
      colorTextPlaceholder: cssVar('--color-text-placeholder'),
      colorBorder: cssVar('--color-border'),
      colorBorderSecondary: cssVar('--color-border-soft'),
      colorBgContainerDisabled: cssVar('--color-surface-muted'),
      fontFamily: cssVar('--font-family'),
      fontSize: 14,
      borderRadius: px('--radius'),
      borderRadiusSM: px('--radius-sm'),
      borderRadiusLG: px('--radius-lg'),
      controlHeight: px('--control-height'),
      boxShadow: cssVar('--shadow-lg'),
    },
  };
};

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setThemeState] = useState<ThemeId>(() => readStoredTheme() ?? DEFAULT_THEME);
  const [antdTheme, setAntdTheme] = useState<ThemeConfig>(readAntdTheme);

  useLayoutEffect(() => {
    document.documentElement.setAttribute('data-theme', theme);
    setAntdTheme(readAntdTheme());
  }, [theme]);

  const setTheme = useCallback((next: ThemeId) => {
    storeTheme(next);
    setThemeState(next);
  }, []);

  const value = useMemo(() => ({ theme, themes: THEMES, setTheme }), [theme, setTheme]);

  return (
    <ThemeContext.Provider value={value}>
      <ConfigProvider theme={antdTheme}>{children}</ConfigProvider>
    </ThemeContext.Provider>
  );
}

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (!context) {
    throw new Error('useTheme должен вызываться внутри ThemeProvider');
  }
  return context;
};
